import { createContext, useContext, useEffect, useState } from "react"
import * as Sentry from "@sentry/browser";
import { iEquipmentContext, iProvider, tGroupedEquipment } from "../types";
import { EquipmentCategory, SingleUnit } from "../components/pages-equipment/equipmentTypes";
import { authClient } from "../utils/amplifyClient";
import { updateSingleUnit as updateSingleUnitMutation } from "../graphql/mutations";
import { useUserContext } from "./UserContext";
import { attachCategoryToUnit, mergeSingleUnitFields, sanitizeGen2SingleUnitWritePayload } from "../components/pages-equipment/equipmentFieldUtils";

export const EquipmentContext = createContext<iEquipmentContext>({
    allEquipmentCategories: [],
    allUnits: [],
    companyEquipmentCategories: [],
    companyUnits: [],
    companyGroupedEquipmentData: [],
    loading: false,
    error: null,
    refreshEquipment: async () => { },
    resetEquipment: () => { },
    addCategory: () => { },
    updateCategory: () => { },
    addUnit: () => { },
    updateUnit: () => { },
    updateUnitInDatabase: async () => { },
});

// eslint-disable-next-line react-refresh/only-export-components
export const useEquipmentContext = () => useContext(EquipmentContext);

export const EquipmentProvider = ({ children }: iProvider) => {

    const { companyUser, companyOwner } = useUserContext();
    const [companyEquipmentCategories, setCompanyEquipmentCategories] = useState<EquipmentCategory[]>([]);
    const [companyUnits, setCompanyUnits] = useState<SingleUnit[]>([]);
    const [companyGroupedEquipmentData, setCompanyGroupedEquipmentData] = useState<tGroupedEquipment[]>([]);
    const [allEquipmentCategories, setAllEquipmentCategories] = useState<EquipmentCategory[]>([]);
    const [allUnits, setAllUnits] = useState<SingleUnit[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId;

    useEffect(() => {
        if (companyId) {
            refreshEquipment();
        } else {
            resetEquipment();
        }
    }, [companyId]);

    const migrateEquipmentToNewStructure = async (): Promise<void> => {
        if (!companyId) return;

        try {
            setLoading(true);

            // Check if categories already exist
            const { data: existingCategories } = await authClient.models.EquipmentCategory.listEquipmentCategoryByCompanyId({ companyId });

            if (existingCategories.length > 0) {
                console.log('Categories already exist, skipping migration');
                return;
            }

            // Fetch old equipment
            const { data: oldEquipment } = await authClient.models.Equipment.listEquipmentByCompanyId({ companyId });

            if (oldEquipment.length === 0) {
                console.log('No equipment to migrate');
                return;
            }

            console.log(`Migrating ${oldEquipment.length} equipment items...`);

            for (const equipment of oldEquipment) {
                // Create EquipmentCategory with same ID
                await authClient.models.EquipmentCategory.create({
                    id: equipment.id,
                    name: equipment.name,
                    companyId: equipment.companyId,
                    formId: equipment.formId,
                    image: equipment.image,
                });

                // Parse units from JSON field
                const units = equipment.units ? (typeof equipment.units === 'string' ? JSON.parse(equipment.units) : equipment.units) : [];

                if (units && units.length > 0) {
                    for (const unit of units) {
                        if (!unit) continue;

                        try {
                            const { data: existingUnit } = await authClient.models.SingleUnit.get({ id: unit.id });
                            if (existingUnit) {
                                console.log(`SingleUnit ${unit.id} already exists, skipping...`);
                                continue;
                            }
                        } catch {
                            // Unit doesn't exist, proceed
                        }

                        try {
                            await authClient.models.SingleUnit.create({
                                id: unit.id,
                                categoryId: equipment.id,
                                name: unit.name,
                                description: unit.description,
                                image: unit.image,
                                inputDate: equipment.createdAt,
                                manufacturerName: unit.manufacturerName,
                                manufactureDate: unit.manufactureDate,
                                aquisitionDate: unit.aquisitionDate,
                                serialNumber: unit.serialNumber,
                                currentMachineHours: unit.currentMachineHours,
                                currentUMCounterValue: 0,
                                machineUMCounterAtAquisition: 0,
                                machineHoursAtAquisition: unit.machineHoursAtAquisition,
                                lastServiceHours: unit.lastServiceHours,
                                downReason: unit.downReason,
                                locationId: unit.locationId,
                                dailyServiceLogFormId: unit.dailyServiceLogFormId,
                                equipmentStatus: true,
                                extraUnitInfo: unit.extraUnitInfo,
                                inputByPerson: 'Migration',
                                lastInspectionDate: '',
                                annualProjectedUsage: 0,
                                putInServiceDate: '',
                                replacedUMCounterStatus: false,
                                replacedUMCounterValue: 0,
                                removedFromServiceDate: '',
                                removedFromServiceReason: '',
                                dispositionReason: '',
                                dispositionDate: '',
                                modelNumber: '',
                                aquisitionCost: 0,
                                licenseNumber: '',
                            });
                            console.log(`Successfully created SingleUnit ${unit.id}`);
                        } catch (unitError) {
                            console.error(`Failed to migrate unit ${unit.id}:`, unitError);
                            Sentry.captureException(unitError);
                        }
                    }
                }
            }

            console.log('Migration completed successfully!');
            await refreshEquipment();

        } catch (error) {
            console.error('Migration failed:', error);
            Sentry.captureException(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllEquipmentData = async (): Promise<void> => {
        try {
            const [categoriesResult, unitsResult] = await Promise.all([
                authClient.models.EquipmentCategory.list(),
                authClient.models.SingleUnit.list(),
            ]);

            setAllEquipmentCategories(categoriesResult.data as EquipmentCategory[]);
            setAllUnits(unitsResult.data);
        } catch (error) {
            console.error('Failed to fetch all equipment data:', error);
            Sentry.captureException(error);
        }
    };

    const refreshEquipment = async (): Promise<void> => {
        if (!companyId) return;
        setLoading(true);
        setError(null);
        try {
            const { data: categories } = await authClient.models.EquipmentCategory.listEquipmentCategoryByCompanyId({ companyId });

            if (categories.length === 0) {
                console.log("migration needed");
                await migrateEquipmentToNewStructure();
                return;
            }

            // Get units for all company categories
            const categoryIds = categories.map(cat => cat.id);
            const unitPromises = categoryIds.map(categoryId =>
                authClient.models.SingleUnit.listSingleUnitsByCategoryId({ categoryId })
            );

            const unitResults = await Promise.all(unitPromises);
            const categoriesList = categories as EquipmentCategory[];
            const singleUnits = unitResults
                .flatMap((result) => result.data)
                .map((unit) => attachCategoryToUnit(unit, categoriesList));

            setCompanyEquipmentCategories(categoriesList);
            setCompanyUnits(singleUnits);

            // Group units by category
            const grouped = categoriesList.map((category) => {
                const categoryUnits = singleUnits.filter((unit) => unit.categoryId === category.id);

                return {
                    ...category,
                    units: categoryUnits
                        .map((unit) => ({
                            ...attachCategoryToUnit(unit, categoriesList),
                            id: unit.id || `unit-${Math.random()}`,
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
                };
            }).sort((a, b) => a.name.localeCompare(b.name));

            setCompanyGroupedEquipmentData(grouped as tGroupedEquipment[]);
            await fetchAllEquipmentData();

        } catch (error) {
            console.error(error);
            Sentry.captureException(error);
            setError('Failed to load equipment data.');
        } finally {
            setLoading(false);
        }
    };

    const resetEquipment = (): void => {
        setCompanyEquipmentCategories([]);
        setCompanyUnits([]);
        setCompanyGroupedEquipmentData([]);
        setLoading(false);
    };

    const updateUnitInDatabase = async (unitId: string, updates: Partial<SingleUnit>) => {
        try {
            const { __typename, createdAt: _createdAt, updatedAt: _updatedAt, owner: _owner, category: _category, tires: _tires, ...cleanUpdates } = updates as any;

            // Strip keys that are not on the deployed Gen2 SingleUnit input (see GEN2_SINGLE_UNIT_WRITABLE_FIELDS).
            const sanitizedUpdates = sanitizeGen2SingleUnitWritePayload(cleanUpdates as Record<string, unknown>);

            if (Object.keys(sanitizedUpdates).length === 0) {
                return;
            }

            const response = (await authClient.graphql({
                query: updateSingleUnitMutation,
                variables: { input: { id: unitId, ...sanitizedUpdates } },
            })) as {
                data?: { updateSingleUnit?: SingleUnit | null };
                errors?: Array<{ message?: string }>;
            };

            const graphqlErrors = response.errors?.map((e) => e.message).filter(Boolean).join('; ');
            if (graphqlErrors) {
                throw new Error(graphqlErrors);
            }

            const updatedUnit = response.data?.updateSingleUnit;
            if (!updatedUnit) {
                throw new Error('Failed to update unit.');
            }

            updateUnit(unitId, mergeSingleUnitFields(
                companyUnits.find((u) => u.id === unitId) ?? ({ id: unitId } as SingleUnit),
                updates,
                updatedUnit,
            ));

            setCompanyGroupedEquipmentData(prev =>
                prev.map(category => ({
                    ...category,
                    units: category.units.map(unit =>
                        unit.id === unitId
                            ? mergeSingleUnitFields(unit, updates, updatedUnit)
                            : unit
                    )
                })) as tGroupedEquipment[]);

        } catch (error) {
            console.error(`Error updating unit ${unitId}:`, error);
            Sentry.captureException(error);
            throw error;
        }
    };

    const addCategory = (category: EquipmentCategory): void => {
        setCompanyEquipmentCategories(prev => [...prev, category]);
    };

    const updateCategory = (id: string, updates: Partial<EquipmentCategory>): void => {
        setCompanyEquipmentCategories(prev => prev.map(cat => cat.id === id ? { ...cat, ...updates } : cat));
    };

    const addUnit = (unit: SingleUnit): void => {
        setCompanyUnits(prev => [...prev, unit]);
        setAllUnits(prev => [...prev, unit]);
    };

    const updateUnit = (id: string, updates: Partial<SingleUnit>): void => {
        setCompanyUnits(prev => prev.map(unit => unit.id === id ? { ...unit, ...updates } : unit));
        setAllUnits(prev => prev.map(unit => unit.id === id ? { ...unit, ...updates } : unit));
    };

    const value: iEquipmentContext = {
        allEquipmentCategories,
        allUnits,
        companyEquipmentCategories,
        companyUnits,
        companyGroupedEquipmentData,
        loading,
        error,
        refreshEquipment,
        resetEquipment,
        addCategory,
        updateCategory,
        addUnit,
        updateUnit,
        updateUnitInDatabase,
    };

    return (
        <EquipmentContext.Provider value={value}>{children}</EquipmentContext.Provider>
    );
};
